<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero-section py-5">
      <div class="container">
        <div class="row align-items-center min-vh-50">
          <div class="col-lg-6">
            <h1 class="display-4 fw-bold mb-4 text-primary">
              Your Complete Women's Health Journey
            </h1>
            <p class="lead mb-4 text-muted">
              Track your fitness goals, monitor your menstrual cycle, connect with healthcare
              professionals, and join a supportive community of women prioritizing their wellbeing.
            </p>
            <div class="d-flex justify-content-center justify-content-lg-start" v-if="!isAuthenticated">
              <router-link to="/register" class="btn btn-primary btn-lg px-5">
                Start Your Journey
              </router-link>
            </div>
          </div>
          <div class="col-lg-6">
            <VideoPlayer :video-src="videoSource" />
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-5 bg-light">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="fw-bold">Comprehensive Health Management</h2>
          <p class="text-muted">Everything you need for your wellness journey</p>
        </div>

        <!-- Fitness & Wellness -->
        <div class="row mb-5">
          <div class="col-12">
            <h3 class="text-center mb-4">Fitness & Wellness</h3>
          </div>
          <div class="col-md-4 mb-3" v-for="feature in fitnessFeatures" :key="feature.id">
            <div class="card h-100 border-0 shadow-sm clickable-card" @click="navigateToFeature(feature)">
              <div class="card-body text-center">
                <div class="feature-icon rounded-circle bg-primary text-white mx-auto mb-3">
                  <i :class="feature.icon"></i>
                </div>
                <h5 class="card-title">{{ feature.title }}</h5>
                <p class="card-text text-muted">{{ feature.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Health Consultation -->
        <div class="row mb-5">
          <div class="col-12">
            <h3 class="text-center mb-4">Health Consultation</h3>
          </div>
          <div class="col-md-4 mb-3" v-for="feature in consultationFeatures" :key="feature.id">
            <div class="card h-100 border-0 shadow-sm clickable-card" @click="navigateToFeature(feature)">
              <div class="card-body text-center">
                <div class="feature-icon rounded-circle bg-success text-white mx-auto mb-3">
                  <i :class="feature.icon"></i>
                </div>
                <h5 class="card-title">{{ feature.title }}</h5>
                <p class="card-text text-muted">{{ feature.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Community & Sharing -->
        <div class="row mb-5">
          <div class="col-12">
            <h3 class="text-center mb-4">Community & Sharing</h3>
          </div>
          <div class="col-md-4 mb-3" v-for="feature in communityFeatures" :key="feature.id">
            <div class="card h-100 border-0 shadow-sm clickable-card" @click="navigateToFeature(feature)">
              <div class="card-body text-center">
                <div class="feature-icon rounded-circle bg-info text-white mx-auto mb-3">
                  <i :class="feature.icon"></i>
                </div>
                <h5 class="card-title">{{ feature.title }}</h5>
                <p class="card-text text-muted">{{ feature.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Events -->
        <div class="row">
          <div class="col-12">
            <h3 class="text-center mb-4">Events</h3>
          </div>
          <div class="col-md-4 mb-3" v-for="feature in eventFeatures" :key="feature.id">
            <div class="card h-100 border-0 shadow-sm clickable-card" @click="navigateToFeature(feature)">
              <div class="card-body text-center">
                <div class="feature-icon rounded-circle bg-warning text-white mx-auto mb-3">
                  <i :class="feature.icon"></i>
                </div>
                <h5 class="card-title">{{ feature.title }}</h5>
                <p class="card-text text-muted">{{ feature.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-5" v-if="!isAuthenticated">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8 text-center">
            <h2 class="fw-bold mb-4">Ready to Start Your Wellness Journey?</h2>
            <p class="lead mb-4 text-muted">
              Join thousands of women who have taken control of their health with FemWellness.
            </p>
            <router-link to="/register" class="btn btn-primary btn-lg">
              Get Started Today
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import VideoPlayer from '@/views/components/VideoPlayer.vue'

export default {
  name: 'Home',
  components: {
    VideoPlayer
  },
  setup() {
    const { isAuthenticated } = useFirebaseAuth()

    return {
      isAuthenticated
    }
  },
  computed: {
    videoSource() {
      // Use environment variable if set, otherwise use default YouTube video
      const externalVideoUrl = process.env.VUE_APP_HOME_VIDEO_URL
      const defaultYouTubeUrl = 'https://youtu.be/iIVBC0GlTjI'

      if (externalVideoUrl) {
        return externalVideoUrl
      }

      // Use default YouTube video
      return defaultYouTubeUrl
    }
  },
  data() {
    return {
      fitnessFeatures: [
        {
          id: 1,
          title: 'Nutrition Tracking',
          description: 'Log meals with photo recognition and calculate calories/nutrients',
          icon: 'bi bi-apple',
          route: '/fitness',
          tab: 'nutrition'
        },
        {
          id: 2,
          title: 'Workout Plans',
          description: 'AI-generated personalized workout recommendations',
          icon: 'bi bi-lightning-charge',
          route: '/fitness',
          tab: 'exercise'
        },
        {
          id: 3,
          title: 'Health Challenges',
          description: 'Join themed activities to boost motivation and engagement',
          icon: 'bi bi-trophy',
          route: '/fitness',
          tab: 'challenges'
        },
      ],
      consultationFeatures: [
        {
          id: 1,
          title: 'Menstrual Tracking',
          description: 'Monitor cycles, mood, and symptoms with AI insights',
          icon: 'bi bi-calendar-heart',
          route: '/health-consultation',
          tab: 'menstrual'
        },
        {
          id: 2,
          title: 'Health Records',
          description: 'Secure storage of medical history and check-up results',
          icon: 'bi bi-file-medical',
          route: '/health-consultation',
          tab: 'health-data'
        },
        {
          id: 3,
          title: 'Doctor Consultation',
          description: 'Direct communication with healthcare professionals',
          icon: 'bi bi-chat-dots',
          route: '/health-consultation',
          tab: 'doctor-chat'
        },
      ],
      communityFeatures: [
        {
          id: 1,
          title: 'Topic Circles',
          description: 'Join groups for pregnancy, weight management, and self-care',
          icon: 'bi bi-people',
          route: '/community',
          tab: 'circles'
        },
        {
          id: 2,
          title: 'Health Videos',
          description: 'Expert-led educational content and Q&A sessions',
          icon: 'bi bi-play-circle',
          route: '/community',
          tab: 'videos'
        },
        {
          id: 3,
          title: 'Achievement System',
          description: 'Earn points and badges for active participation',
          icon: 'bi bi-award',
          route: '/community',
          tab: 'achievements'
        },
      ],
      eventFeatures: [
        {
          id: 1,
          title: 'Event Registration',
          description: 'Book fitness classes, workshops, and health seminars',
          icon: 'bi bi-calendar-event',
          route: '/events',
          tab: 'registration'
        },
        {
          id: 2,
          title: 'Maps & Locations',
          description: 'Find nearby events with integrated mapping',
          icon: 'bi bi-geo-alt',
          route: '/events',
          tab: 'map'
        },
        {
          id: 3,
          title: 'Social Features',
          description: 'Connect with participants before and after events',
          icon: 'bi bi-share',
          route: '/events',
          tab: 'social'
        },
      ],
    }
  },
  methods: {
    navigateToFeature(feature) {
      // Allow access to Events page for all users
      if (feature.route === '/events') {
        this.$router.push({
          path: feature.route,
          query: { tab: feature.tab }
        })
        return
      }
      
      // For other features, check if user is authenticated
      if (!this.isAuthenticated) {
        this.$router.push('/login')
        return
      }
      
      // Navigate to feature if authenticated
      this.$router.push({
        path: feature.route,
        query: { tab: feature.tab }
      })
    },
  },
}
</script>

<style scoped>
.hero-section {
  background: linear-gradient(135deg, #fef7f7 0%, #f3e5f5 100%);
}

.min-vh-50 {
  min-height: 50vh;
}


.feature-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.card {
  transition: transform 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
}

.clickable-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.btn-outline-primary {
  color: var(--primary-color);
  border-color: var(--primary-color);
}

.text-primary {
  color: var(--primary-color) !important;
}

@media (max-width: 768px) {
  .display-4 {
    font-size: 2rem;
  }
}
</style>
